import React, { useMemo } from 'react';
import { Button, Checkbox, Form, Input, message, Switch } from 'antd';
import s from './s.module.less';
import { useNavigate } from 'react-router-dom';
import useNotificationDetail from 'hooks/useNotificationDetail';
import commonS from 'styles/common.module.less';
import ProviderStore from 'store/Provider';
import { ENotificationType, TNotificationRecipient, TNotificationType } from 'types/common';
import { addNotificationType, removeNotificationType, setNotificationRecipients, toggleNotificationType } from 'api/provider';
import { Provider, EFeatureType } from 'types/provider';
import HomeStore from 'store/Home';

export type FormEmails = {
    list: { email: string }[],
};

const EmailNotificationSettings = () => {
    const [isEdit, setIsEdit] = React.useState(false);
    const [detail, loading, refetch] = useNotificationDetail();
    const [formInstance] = Form.useForm<FormEmails>();
    const [get, set] = ProviderStore.useStore();
    const provider: Provider = get('data') as Provider;
    const [saving, setSaving] = React.useState(false);
    const navigate = useNavigate();
    const [visitBookedOn, setVisitBookedOn] = React.useState(false);
    const [patientMessageOn, setPatientMessageOn] = React.useState(false);
    const [textVisitOn, setTextVisitOn] = React.useState(false);
    const [switchingVisitBooked, setSwitchingVisitBooked] = React.useState(false);
    const [switchingPatientMessage, setSwitchingPatientMessage] = React.useState(false);
    const [switchingTextVisit, setSwitchingTextVisit] = React.useState(false);
    const [isEditingTypeRecipientId, setIsEditingTypeRecipientId] = React.useState<number | null>(null);
    const [getHomeStore] = HomeStore.useStore();
    const homeInfo = getHomeStore('data');
    const showTextVisitNotification = !!homeInfo?.features?.includes(EFeatureType.TEXT_VISIT);

    const handleSave = async () => {
        try {
            const values = await formInstance.validateFields();
            setSaving(true);
            const res = await setNotificationRecipients(values.list.map((item: { email: string }) => item.email), provider.id);
            if (!res.error) {
                message.success('Notification recipients updated successfully.');
                setIsEdit(false);
                refetch();
            }
            setSaving(false);
        } catch (e) {
            console.error(e);
        }
    };

    const handleCancel = () => {
        setIsEdit(false);
    };

    const emailDisplay = useMemo(() => {
        return detail?.recipients?.map((recipient: TNotificationRecipient) => recipient.email).join(', ');
    }, [detail?.recipients]);

    const book = detail?.notificationTypes?.find((item: TNotificationType) => item.notificationType === ENotificationType.NEW_VISIT_BOOKED);
    const patientMessage = detail?.notificationTypes?.find((item: TNotificationType) => item.notificationType === ENotificationType.NEW_PATIENT_MESSAGE);
    const textVisit = detail?.notificationTypes?.find((item: TNotificationType) => item.notificationType === ENotificationType.NEW_TEXT_VISIT);

    const handleTypeEnableChange = async (type: ENotificationType, enabled: boolean) => {
        if (type === ENotificationType.NEW_VISIT_BOOKED) {
            setSwitchingVisitBooked(true);
            const res = await toggleNotificationType(type, provider.id, enabled);
            if (!res.error) {
                message.success('Changes saved successfully');
                if (!enabled) {
                    setVisitBookedOn(false);
                }
                refetch();
            }
            setSwitchingVisitBooked(false);
        }
        if (type === ENotificationType.NEW_PATIENT_MESSAGE) {
            setSwitchingPatientMessage(true);
            const res = await toggleNotificationType(type, provider.id, enabled);
            if (!res.error) {
                message.success('Changes saved successfully');
                if (!enabled) {
                    setPatientMessageOn(false);
                }
                refetch();
            }
            setSwitchingPatientMessage(false);
        }
        if (type === ENotificationType.NEW_TEXT_VISIT) {
            setSwitchingTextVisit(true);
            const res = await toggleNotificationType(type, provider.id, enabled);
            if (!res.error) {
                message.success('Changes saved successfully');
                if (!enabled) {
                    setTextVisitOn(false);
                }
                refetch();
            }
            setSwitchingTextVisit(false);
        }
    };

    const handleTypeRecipientChange = async (type: ENotificationType, recipientId: number, checked: boolean) => {
        if (type === ENotificationType.NEW_VISIT_BOOKED) {
            setIsEditingTypeRecipientId(recipientId);
            if (checked) {
                const res = await addNotificationType(book?.id!, recipientId);
                if (!res.error) {
                    message.success('Changes saved successfully');
                    refetch();
                }
            } else {
                const res = await removeNotificationType(book?.id!, recipientId);
                if (!res.error) {
                    message.success('Changes saved successfully');
                    refetch();
                }
            }
            setIsEditingTypeRecipientId(null);
        }
        if (type === ENotificationType.NEW_PATIENT_MESSAGE) {
            setIsEditingTypeRecipientId(recipientId);
            if (checked) {
                const res = await addNotificationType(patientMessage?.id!, recipientId);
                if (!res.error) {
                    message.success('Changes saved successfully');
                    refetch();
                }
            } else {
                const res = await removeNotificationType(patientMessage?.id!, recipientId);
                if (!res.error) {
                    message.success('Changes saved successfully');
                    refetch();
                }
            }
            setIsEditingTypeRecipientId(null);
        }
        if (type === ENotificationType.NEW_TEXT_VISIT) {
            setIsEditingTypeRecipientId(recipientId);
            if (checked) {
                const res = await addNotificationType(textVisit?.id!, recipientId);
                if (!res.error) {
                    message.success('Changes saved successfully');
                    refetch();
                }
            } else {
                const res = await removeNotificationType(textVisit?.id!, recipientId);
                if (!res.error) {
                    message.success('Changes saved successfully');
                    refetch();
                }
            }
            setIsEditingTypeRecipientId(null);
        }
    };

    return (
        <div className={s.wrap}>
            <div className={s.top}>
                <div className={s.topHeader}>
                    <span>Email recipients</span>
                    {
                        !isEdit && (
                            <Button
                                onClick={() => {
                                    setIsEdit(true);
                                    formInstance.resetFields();
                                    formInstance.setFieldsValue({
                                        list: detail?.recipients?.map((recipient: TNotificationRecipient) => ({
                                            email: recipient.email,
                                        })),
                                    });
                                }}
                                className={s.btn}
                                size="middle"
                            >Edit
                            </Button>
                        )
                    }
                    {
                        isEdit && (
                            <div className={s.btnWrap}>
                                <Button
                                    onClick={handleCancel}
                                    size="middle"
                                    className={s.btn}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="middle"
                                    type="primary"
                                    onClick={handleSave}
                                    className={s.btn}
                                    loading={saving}
                                    style={{
                                        marginLeft: '8px',
                                    }}
                                >Save
                                </Button>
                            </div>
                        )
                    }
                </div>
                {
                    !isEdit && (
                        <div className={s.topContent}>
                            <div className={s.des}>
                                Add up to 5 email addresses to receive notifications — teammates, assistants, or a shared inbox — so the right people stay in the loop
                            </div>
                            <div className={s.recipients}>
                                {emailDisplay}
                            </div>
                        </div>
                    )
                }
                {
                    isEdit && (
                        <div className={s.topContent}>
                            <div className={s.des}>
                                Add up to 5 email addresses to receive notifications — teammates, assistants, or a shared inbox — so the right people stay in the loop
                            </div>
                            <Form
                                form={formInstance}
                                name="basic"
                                className={commonS.formStyle1}
                                autoComplete="off"
                                layout="vertical"
                                scrollToFirstError
                            >
                                <Form.List name="list">
                                    {
                                        (fields, { add, remove }) => {
                                            return (
                                                <>
                                                    {
                                                        fields.map(({ key, name, ...restField }, index) => {
                                                            return (
                                                                <div className={s.formBody}>
                                                                    <div className={s.row}>
                                                                        <Form.Item
                                                                            {...restField}
                                                                            rules={[
                                                                                { required: true, message: 'Email address is required' },
                                                                                { type: 'email', message: 'Please enter a valid email format' },
                                                                            ]}
                                                                            name={[name, 'email']}

                                                                        >
                                                                            <Input style={{ width: '360px' }} />
                                                                        </Form.Item>
                                                                        <span className={s.remove} onClick={() => remove(index)}>
                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                                <path d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                            </svg>
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                    {
                                                        fields?.length < 5 &&
                                                        <Form.Item>
                                                            <div className={s.add} onClick={() => add()}>Add {fields?.length > 0 ? 'another' : 'an'} email address</div>
                                                        </Form.Item>
                                                    }
                                                </>
                                            );
                                        }
                                    }
                                </Form.List>
                            </Form>
                        </div>
                    )
                }
            </div>
            <div className={s.mid}>
                <h3>Patient Care</h3>
                <div className={`${s.item} ${visitBookedOn ? s.itemOn : ''}`}>
                    <div className={s.iHeader}>
                        <div className={s.l}>
                            <Switch
                                onChange={() => {
                                    handleTypeEnableChange(ENotificationType.NEW_VISIT_BOOKED, !book?.enabled);
                                }}
                                checked={book?.enabled}
                                loading={switchingVisitBooked}
                            />
                            <div className={s.info}>
                                <div className={s.iTitle}>
                                    New visit booked
                                </div>
                                <div className={s.iDesc}>
                                    Get notified when a new visit is booked
                                </div>
                            </div>
                        </div>
                        {
                            book?.enabled && (
                                <div
                                    onClick={() => {
                                        setVisitBookedOn(!visitBookedOn);
                                    }}
                                    className={s.r}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M19 9L12 16L5 9" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            )
                        }
                    </div>
                    <div className={s.iContent}>
                        {
                            detail?.recipients.map((recipient: TNotificationRecipient) => {
                                const checked = book?.recipientIds.includes(recipient.id);
                                return (
                                    <div className={s.recipient} key={recipient.id}>
                                        <Checkbox
                                            checked={checked}
                                            onChange={(e) => {
                                                handleTypeRecipientChange(ENotificationType.NEW_VISIT_BOOKED, recipient.id, e.target.checked);
                                            }}
                                            disabled={isEditingTypeRecipientId === recipient.id || (book?.recipientIds.length === 1 && checked)}
                                        /> {recipient.email}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                <div className={`${s.item} ${patientMessageOn ? s.itemOn : ''}`}>
                    <div className={s.iHeader}>
                        <div className={s.l}>
                            <Switch
                                onChange={() => {
                                    handleTypeEnableChange(ENotificationType.NEW_PATIENT_MESSAGE, !patientMessage?.enabled);
                                }}
                                checked={patientMessage?.enabled}
                                loading={switchingPatientMessage}
                            />
                            <div className={s.info}>
                                <div className={s.iTitle}>
                                    New patient message
                                </div>
                                <div className={s.iDesc}>
                                    Receive alerts when a patient sends you a message.
                                </div>
                            </div>
                        </div>
                        {
                            patientMessage?.enabled && (
                                <div
                                    onClick={() => {
                                        setPatientMessageOn(!patientMessageOn);
                                    }}
                                    className={s.r}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M19 9L12 16L5 9" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            )
                        }
                    </div>
                    <div className={s.iContent}>
                        {
                            detail?.recipients.map((recipient: TNotificationRecipient) => {
                                const checked = patientMessage?.recipientIds.includes(recipient.id);
                                return (
                                    <div className={s.recipient} key={recipient.id}>
                                        <Checkbox
                                            checked={checked}
                                            onChange={(e) => {
                                                handleTypeRecipientChange(ENotificationType.NEW_PATIENT_MESSAGE, recipient.id, e.target.checked);
                                            }}
                                            disabled={isEditingTypeRecipientId === recipient.id || (patientMessage?.recipientIds.length === 1 && checked)}
                                        /> {recipient.email}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                {
                    showTextVisitNotification && (
                        <div className={`${s.item} ${textVisitOn ? s.itemOn : ''}`}>
                            <div className={s.iHeader}>
                                <div className={s.l}>
                                    <Switch
                                        onChange={() => {
                                            handleTypeEnableChange(ENotificationType.NEW_TEXT_VISIT, !textVisit?.enabled);
                                        }}
                                        checked={textVisit?.enabled}
                                        loading={switchingTextVisit}
                                    />
                                    <div className={s.info}>
                                        <div className={s.iTitle}>
                                            New text visit cases
                                            <span className={s.newBadge}>NEW</span>
                                        </div>
                                        <div className={s.iDesc}>
                                            Get notified when new text visit cases are ready to be picked up in your licensed states.
                                        </div>
                                    </div>
                                </div>
                                {
                                    textVisit?.enabled && (
                                        <div
                                            onClick={() => {
                                                setTextVisitOn(!textVisitOn);
                                            }}
                                            className={s.r}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M19 9L12 16L5 9" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    )
                                }
                            </div>
                            <div className={s.iContent}>
                                {
                                    detail?.recipients.map((recipient: TNotificationRecipient) => {
                                        const checked = textVisit?.recipientIds.includes(recipient.id);
                                        return (
                                            <div className={s.recipient} key={recipient.id}>
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        handleTypeRecipientChange(ENotificationType.NEW_TEXT_VISIT, recipient.id, e.target.checked);
                                                    }}
                                                    disabled={isEditingTypeRecipientId === recipient.id || (textVisit?.recipientIds.length === 1 && checked)}
                                                /> {recipient.email}
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default EmailNotificationSettings;
